import { LOGIN_OAUTH, LOGIN_URL } from 'core/crud/auth.crud';
import { removeAllStorage } from './utils';

// Global registry to store pending requests and deduplicate them
const pendingRequests = new Map();

/**
 * Creates a unique key for a request based on method, URL, and params
 * @param {Object} config - Axios request config
 * @returns {string} - Unique key for the request
 */
function getRequestKey(config) {
  const { method, url, params, data } = config;
  return `${method}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`;
}

export function setupAxios(axios, baseUrl, store) {
  axios.interceptors.request.use(
    (config) => {
      const {
        auth: { authToken, loginContext /*, tokenExpiration, user*/ },
      } = store.getState();
      // const hostname = window?.location?.hostname;
      // const isSantillanaAlfa = hostname.includes(SANTILLANA_ALFA);
      if (authToken) {
        config.headers.Authorization = `${authToken}`;
      }
      if (loginContext) {
        config.headers['login_context'] = loginContext;
      }
      // if (authToken && !checkTokenIsValid(tokenExpiration)) {
      //   console.log('invalid token, go to logout ', authToken);
      //   //If token is expired, we intercept the request before send to the api and logout the user
      //   removeAllStorage();
      //   window.location.href = user.referrer ? user.referrer : '/auth/login';
      //   throw new axios.Cancel('Token is expired');
      // }
      config.baseURL = baseUrl;
      return config;
    },
    (err) => Promise.reject(err)
  );

  // REQUEST DEDUPLICATION: Wrap all HTTP methods to deduplicate identical pending requests
  const wrapMethod = (method, originalMethod) => {
    return function (...args) {
      // Build a config object from the method arguments
      let config = {};
      if (method === 'get' || method === 'delete' || method === 'head' || method === 'options') {
        // These methods have signature: (url, config)
        config = {
          method,
          url: args[0],
          ...(args[1] || {}),
        };
      } else {
        // post, put, patch have signature: (url, data, config)
        config = {
          method,
          url: args[0],
          data: args[1],
          ...(args[2] || {}),
        };
      }

      const requestKey = getRequestKey(config);

      // If an identical request is already in progress, return the existing promise
      if (pendingRequests.has(requestKey)) {
        return pendingRequests.get(requestKey);
      }

      // Create new request and store the promise
      const requestPromise = originalMethod.apply(axios, args)
        .then((response) => {
          // Clean up after successful response
          pendingRequests.delete(requestKey);
          return response;
        })
        .catch((error) => {
          // Clean up after error
          pendingRequests.delete(requestKey);
          throw error;
        });

      // Store the promise for deduplication
      pendingRequests.set(requestKey, requestPromise);

      return requestPromise;
    };
  };

  // Wrap all HTTP methods
  const originalGet = axios.get.bind(axios);
  const originalPost = axios.post.bind(axios);
  const originalPut = axios.put.bind(axios);
  const originalPatch = axios.patch.bind(axios);
  const originalDelete = axios.delete.bind(axios);

  axios.get = wrapMethod('get', originalGet);
  axios.post = wrapMethod('post', originalPost);
  axios.put = wrapMethod('put', originalPut);
  axios.patch = wrapMethod('patch', originalPatch);
  axios.delete = wrapMethod('delete', originalDelete);

  axios.interceptors.response.use(
    function (response) {
      // response data
      return response;
    },
    function (error) {
      const {
        auth: { user },
      } = store.getState();
      if (error.response && error.response.status === 401) {
        // A 401 on login endpoints means wrong credentials, not an expired session.
        // Let the error propagate to the caller so it can display a toast.
        const isLoginRequest = [LOGIN_URL, LOGIN_OAUTH].includes(error.config?.url);
        if (!isLoginRequest) {
          removeAllStorage();
          window.location.href = '/logout';
        }
      }
      if (error.response && error.response.status === 403) {
        // A 403 on a course detail endpoint means no license for that course, not an auth failure.
        // useCourse handles it with a toast + redirect to /home instead of logging the user out.
        const isCourseWithoutLicense = /front\/courses\/[^/]+$/.test(error.config?.url);
        if (!isCourseWithoutLicense) {
          removeAllStorage();
          window.location.href = user.referrer ? user.referrer : '/logout';
        }
      }
      return { error: error.response };
    }
  );
}

